import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Typography,
  Paper,
  Button,
  Alert,
  Box,
  Stack,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  Menu,
  MenuItem,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import ViewAgendaOutlinedIcon from '@mui/icons-material/ViewAgendaOutlined';
import ViewModuleOutlinedIcon from '@mui/icons-material/ViewModuleOutlined';
import SortOutlinedIcon from '@mui/icons-material/SortOutlined';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import { useSearchParams } from 'react-router-dom';
import { useAuth, usePermissions } from '../AuthContext';
import { alpha } from '@mui/material/styles';
import {
  collection,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  where,
  limit,
  startAfter,
  getDocs,
  getDoc,
  doc,
  getCountFromServer,
} from 'firebase/firestore';
import AddLeadDrawer from '../components/AddLeadDrawer';
import LeadDetailDrawer from '../components/LeadDetailDrawer';
import LeadCompactCard, { AppointmentBadge } from '../components/LeadCompactCard';
import { useRef } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import { isCareerApplicationSource } from '../utils/careerApplications';
import { useMyWhatsAppUnread } from '../components/WhatsAppNotificationPanel';

type LeadStatus = string;
const NULL_STATUS_KEY = '__NULL_STATUS__';

function buildTrigrams(value: string): string[] {
  const s = String(value || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');
  if (!s) return [];
  const out = new Set<string>();
  const padded = ` ${s} `;
  for (let i = 0; i + 3 <= padded.length; i += 1) {
    const gram = padded.slice(i, i + 3);
    if (!gram.trim()) continue;
    out.add(gram);
  }
  return Array.from(out);
}

function parseDateLoose(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value !== 'string') return null;

  const s = value.trim();
  if (!s) return null;

  // ISO / RFC3339 (what our import scripts write): "2026-02-08T12:34:56.000Z"
  // Only try Date.parse for ISO-ish strings to avoid locale ambiguity.
  if (/[tT]/.test(s) || /^\d{4}-\d{2}-\d{2}/.test(s)) {
    const ms = Date.parse(s);
    if (!Number.isNaN(ms)) return new Date(ms);
  }

  // Support: "DD/MM/YY", "DD/MM/YYYY", "DD-MM-YYYY", and optional " HH:MM:SS"
  const m = s.match(
    /^(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (!m) return null;
  const dd = Number(m[1]);
  const mm = Number(m[2]);
  const yyRaw = m[3];
  const yyyy = yyRaw.length === 2 ? 2000 + Number(yyRaw) : Number(yyRaw);
  const hh = m[4] ? Number(m[4]) : 0;
  const min = m[5] ? Number(m[5]) : 0;
  const ss = m[6] ? Number(m[6]) : 0;

  const d = new Date(yyyy, mm - 1, dd, hh, min, ss, 0);
  if (Number.isNaN(d.getTime())) return null;
  // Basic sanity check: keep parsing strict enough to avoid JS rollover (e.g. 32/13/2024).
  if (d.getFullYear() !== yyyy || d.getMonth() !== mm - 1 || d.getDate() !== dd) return null;
  return d;
}

function safeStatusColor(value?: string | null): string | null {
  const c = String(value || '').trim();
  return /^#([0-9a-fA-F]{6})$/.test(c) ? c : null;
}

function toMillisLoose(value: unknown): number {
  if (!value) return 0;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? 0 : value.getTime();
  // Firestore Timestamp (client SDK)
  if (typeof (value as any)?.toDate === 'function') {
    const d = (value as any).toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
  }
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const d = parseDateLoose(value);
  return d ? d.getTime() : 0;
}

function isoRangeForYMD(opts: {
  year: number;
  month?: number | null; // 1-12
  day?: number | null; // 1-31
}): { startIso: string; endIso: string } | null {
  const { year, month, day } = opts;
  if (!year || Number.isNaN(year)) return null;

  if (!month) {
    // Use local day boundaries so the date tree + query match what users see in their timezone.
    const start = new Date(year, 0, 1, 0, 0, 0, 0);
    const end = new Date(year + 1, 0, 1, 0, 0, 0, 0);
    return { startIso: start.toISOString(), endIso: end.toISOString() };
  }
  if (!day) {
    const start = new Date(year, month - 1, 1, 0, 0, 0, 0);
    const end = new Date(year, month, 1, 0, 0, 0, 0);
    return { startIso: start.toISOString(), endIso: end.toISOString() };
  }

  const start = new Date(year, month - 1, day, 0, 0, 0, 0);
  const end = new Date(year, month - 1, day + 1, 0, 0, 0, 0);
  return { startIso: start.toISOString(), endIso: end.toISOString() };
}

function tsRangeForYMD(opts: {
  year: number;
  month?: number | null; // 1-12
  day?: number | null; // 1-31
}): { start: Timestamp; end: Timestamp } | null {
  const r = isoRangeForYMD(opts);
  if (!r) return null;
  return { start: Timestamp.fromDate(new Date(r.startIso)), end: Timestamp.fromDate(new Date(r.endIso)) };
}

type Lead = {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  source?: string;
  status: LeadStatus;
  assignedTo?: string;
  assignedToName?: string | null;
  nextFollowUpDate?: Timestamp;
  notes?: string | string[];
  createdAt?: Timestamp;
  updatedAt?: Timestamp | null;
  rating?: number | null;
  appointment?: {
    type?: 'site-visit' | 'office-meeting';
    scheduledAt?: Timestamp;
    status?: 'scheduled' | 'completed' | 'cancelled' | 'no-show';
  } | null;
};

export default function AllLeadsPage() {
  const { canAction } = usePermissions();
  const { roleId } = useAuth();
  const waUnreadByPhone = useMyWhatsAppUnread();
  const db = useMemo(() => getFirestore(), []);

  const [leads, setLeads] = useState<Lead[]>([]);
  const [statuses, setStatuses] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [detailLeadId, setDetailLeadId] = useState<string | null>(null);
  const [detailLeadFallback, setDetailLeadFallback] = useState<Lead | null>(null);
  const [detailWidth, setDetailWidth] = useState(520);
  const paperRef = useRef<HTMLDivElement | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeCooldownUntil, setResizeCooldownUntil] = useState(0);
  const [searchRaw, setSearchRaw] = useState('');
  const [search, setSearch] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [moreAnchorEl, setMoreAnchorEl] = useState<HTMLElement | null>(null);
  const moreMenuOpen = Boolean(moreAnchorEl);
  const [sortAnchorEl, setSortAnchorEl] = useState<HTMLElement | null>(null);
  const sortMenuOpen = Boolean(sortAnchorEl);
  const [sortMode, setSortMode] = useState<'createdAtDesc' | 'updatedAtDesc'>('createdAtDesc');
  const [createdFilterYear, setCreatedFilterYear] = useState<number | null>(null);
  const [createdFilterMonth, setCreatedFilterMonth] = useState<number | null>(null);
  const [createdFilterDay, setCreatedFilterDay] = useState<number | null>(null);
  const [createdFilterStatus, setCreatedFilterStatus] = useState<string | null>(null);
  const [expandedYears, setExpandedYears] = useState<Record<string, boolean>>({});
  const [expandedMonths, setExpandedMonths] = useState<Record<string, boolean>>({});
  const [expandedDays, setExpandedDays] = useState<Record<string, boolean>>({});
  const [expandedTopStatus, setExpandedTopStatus] = useState(false);
  const [dateIndexRows, setDateIndexRows] = useState<Array<{ createdAt: any; status: string }>>([]);
  const [dateIndexTruncated, setDateIndexTruncated] = useState(false);
  const [siteVisitCountByLead, setSiteVisitCountByLead] = useState<Record<string, number>>({});

  const isAdmin = useMemo(() => {
    const r = String(roleId || '').toLowerCase();
    return r === 'admin' || r === 'super-admin';
  }, [roleId]);

  // Default to list view for everyone; only admins can toggle and persist a preference.
  const [leadListView, setLeadListView] = useState<'card' | 'sheet'>('sheet');

  useEffect(() => {
    if (!isAdmin) {
      setLeadListView('sheet');
      return;
    }
    try {
      const v = localStorage.getItem('leadListView');
      setLeadListView(v === 'card' ? 'card' : 'sheet');
    } catch {
      // ignore
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    try {
      localStorage.setItem('leadListView', leadListView);
    } catch {
      // ignore
    }
  }, [isAdmin, leadListView]);

  // Pagination (keeps the initial load fast for large datasets).
  const [pageSize, setPageSize] = useState<50 | 100>(50);
  const [lastDoc, setLastDoc] = useState<any | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const isSortNonDefault = sortMode !== 'createdAtDesc';
  const isSortActive = sortMenuOpen || isSortNonDefault;
  const hasCreatedDateFilter = !!createdFilterYear;
  const hasStatusFilter = !!createdFilterStatus;
  const hasActiveFilters =
    Boolean(search.trim()) || isSortNonDefault || hasCreatedDateFilter || hasStatusFilter;
  const sortSummaryLabel = sortMode === 'updatedAtDesc' ? 'Updated \u2193' : 'Created \u2193';
  const activeFiltersSummary = useMemo(() => {
    const parts: string[] = [];
    if (isSortNonDefault) parts.push(sortSummaryLabel);
    if (createdFilterYear) {
      const base = `${createdFilterYear}${createdFilterMonth ? `/${createdFilterMonth}` : ''}${
        createdFilterDay ? `/${createdFilterDay}` : ''
      }`;
      parts.push(base);
    }
    if (createdFilterStatus) {
      const s = createdFilterStatus === NULL_STATUS_KEY
        ? 'No Status'
        : (statuses.find((x: any) => x.id === createdFilterStatus)?.label as string) ||
          createdFilterStatus;
      parts.push(`Status: ${s}`);
    }
    return parts.length ? ` \u2022 ${parts.join(' \u2022 ')}` : '';
  }, [
    createdFilterDay,
    createdFilterMonth,
    createdFilterStatus,
    createdFilterYear,
    isSortNonDefault,
    sortSummaryLabel,
    statuses,
  ]);

  const detailLead = useMemo(
    () =>
      detailLeadId
        ? (leads.find((l) => l.id === detailLeadId) || detailLeadFallback || null)
        : null,
    [detailLeadFallback, detailLeadId, leads]
  );

  const urlLeadId = useMemo(() => searchParams.get('leadId'), [searchParams]);

  const setUrlLeadId = useCallback((id: string | null, replace: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('leadId', id);
    else next.delete('leadId');
    setSearchParams(next, { replace });
  }, [searchParams, setSearchParams]);

  const openLeadDetails = useCallback((id: string) => {
    setDetailLeadId(id);
    setDetailDrawerOpen(true);
    // If already open, avoid stacking history entries when switching between leads.
    setUrlLeadId(id, !!detailLeadId);
  }, [detailLeadId, setUrlLeadId]);

  const closeLeadDetails = useCallback(() => {
    setDetailDrawerOpen(false);
    // Close via UI should not create an extra history entry (avoid "back reopens drawer").
    setUrlLeadId(null, true);
  }, [setUrlLeadId]);

  const handleLeadDrawerExited = useCallback(() => {
    setDetailLeadId(null);
    setDetailLeadFallback(null);
  }, []);

  // Sync drawer open/close with URL param so mobile back closes the drawer instead of leaving the page.
  useEffect(() => {
    if (!urlLeadId) {
      setDetailDrawerOpen(false);
      return;
    }
    if (urlLeadId !== detailLeadId) setDetailLeadId(urlLeadId);
    setDetailDrawerOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlLeadId]);

  // If the lead isn't in the currently loaded page (pagination), fetch it for the drawer.
  useEffect(() => {
    const id = detailLeadId;
    if (!id) return;
    const inList = leads.some((l) => l.id === id);
    if (inList) {
      setDetailLeadFallback(null);
      return;
    }
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'leads', id));
        if (!snap.exists()) return;
        setDetailLeadFallback({ id: snap.id, ...(snap.data() as any) } as Lead);
      } catch (e) {
        console.error('Failed to fetch lead for drawer', e);
      }
    })();
  }, [db, detailLeadId, leads]);

  const sortField = useMemo<'updatedAt' | 'createdAt'>(
    () => (sortMode === 'updatedAtDesc' ? 'updatedAt' : 'createdAt'),
    [sortMode]
  );

  const searchKind = useMemo(() => {
    const q = search.trim();
    if (!q) return { kind: 'none' as const, value: '' };
    const digitsRaw = q.replace(/\D/g, '');
    const digits = digitsRaw;
    if (digits.length >= 10) return { kind: 'phone' as const, value: digits.slice(-10) };
    if (digits.length >= 3 && digits.length <= 9) {
      // Support substring search for phone by storing `phoneSearch` parts on lead docs.
      // If user types country code prefix, drop it for partial searches.
      let v = digits;
      if (v.startsWith('91')) v = v.slice(2);
      if (v.length < 3) return { kind: 'none' as const, value: '' };
      return { kind: 'phonePart' as const, value: v };
    }
    if (q.includes('@')) return { kind: 'email' as const, value: q.toLowerCase() };
    const lower = q.toLowerCase();
    if (lower.length >= 3) return { kind: 'namePart' as const, value: lower };
    return { kind: 'name' as const, value: lower };
  }, [search]);

  const isSearchMode = searchKind.kind !== 'none';

  // When searching, show New leads first, then other non-closed, then Closed at the end.
  // This is client-side only (quick win) so ordering is guaranteed within the currently loaded results.
  const groupLeadsForSearch = (items: Lead[]) => {
    if (!isSearchMode) return items;
    const statusNorm = (l: any) => String(l?.status || '').toLowerCase().trim();
    const isClosed = (l: any) => {
      const n = statusNorm(l);
      return n === 'closed' || n === 'completed';
    };
    const isNew = (l: any) => statusNorm(l) === 'new';
    const news: Lead[] = [];
    const openOther: Lead[] = [];
    const closed: Lead[] = [];
    for (const l of items) {
      if (isClosed(l)) closed.push(l);
      else if (isNew(l)) news.push(l);
      else openOther.push(l);
    }
    return [...news, ...openOther, ...closed];
  };

  const filterPropertyLeads = useCallback(
    (items: Lead[]) => items.filter((item: any) => (
      !isCareerApplicationSource(item.source) &&
      !item.isMerged &&
      !item.archived
    )),
    []
  );

  const clearSearch = () => {
    // Clear both raw + debounced state so filters become clickable immediately.
    setSearchRaw('');
    setSearch('');
    setMobileSearchOpen(false);
  };

  const baseLeadsQuery = useMemo(() => {
    const leadsRef = collection(db, 'leads');
    const parts: any[] = [];
    const effectiveLimit = searchKind.kind === 'namePart' ? Math.max(pageSize * 3, pageSize) : pageSize;
    if (isSearchMode) {
      if (searchKind.kind === 'phone') {
        parts.push(where('phoneSearch', 'array-contains', searchKind.value));
        parts.push(orderBy(sortField, 'desc'));
        if (sortField !== 'createdAt') parts.push(orderBy('createdAt', 'desc'));
      } else if (searchKind.kind === 'phonePart') {
        // Phone "contains" search via precomputed substrings stored on the lead doc.
        // Import/creation code populates `phoneSearch` for 3-10 digit substrings of phone10.
        parts.push(where('phoneSearch', 'array-contains', searchKind.value));
        parts.push(orderBy(sortField, 'desc'));
        if (sortField !== 'createdAt') parts.push(orderBy('createdAt', 'desc'));
      } else if (searchKind.kind === 'email') {
        parts.push(where('emailLower', '==', searchKind.value));
        parts.push(orderBy(sortField, 'desc'));
        if (sortField !== 'createdAt') parts.push(orderBy('createdAt', 'desc'));
      } else if (searchKind.kind === 'namePart') {
        const grams = buildTrigrams(searchKind.value).slice(0, 10);
        if (!grams.length) {
          parts.push(where('nameLower', '>=', searchKind.value));
          parts.push(where('nameLower', '<=', `${searchKind.value}\uf8ff`));
          parts.push(orderBy('nameLower', 'asc'));
        } else {
          // "Contains" search: coarse trigram match in Firestore + exact `includes` filter client-side.
          parts.push(where('nameTrigrams', 'array-contains-any', grams));
          parts.push(orderBy(sortField, 'desc'));
          if (sortField !== 'createdAt') parts.push(orderBy('createdAt', 'desc'));
        }
      } else {
        // Prefix search by normalized name
        // NOTE: Requires `nameLower` to exist on lead docs.
        parts.push(where('nameLower', '>=', searchKind.value));
        parts.push(where('nameLower', '<=', `${searchKind.value}\uf8ff`));
        parts.push(orderBy('nameLower', 'asc'));
      }
    } else {
      const r =
        createdFilterYear &&
        tsRangeForYMD({ year: createdFilterYear, month: createdFilterMonth, day: createdFilterDay });
      if (r) {
        parts.push(where('createdAt', '>=', r.start));
        parts.push(where('createdAt', '<', r.end));
        if (createdFilterStatus && createdFilterStatus !== NULL_STATUS_KEY) {
          parts.push(where('status', '==', createdFilterStatus));
        }
        // When we filter by received date, keep ordering consistent (required for range queries).
        parts.push(orderBy('createdAt', 'desc'));
        // Optional secondary ordering inside the same received-date bucket.
        if (sortField === 'updatedAt') parts.push(orderBy('updatedAt', 'desc'));
      } else {
        if (createdFilterStatus && createdFilterStatus !== NULL_STATUS_KEY) {
          parts.push(where('status', '==', createdFilterStatus));
        }
        parts.push(orderBy(sortField, 'desc'));
        if (sortField !== 'createdAt') parts.push(orderBy('createdAt', 'desc'));
      }
    }
    parts.push(limit(effectiveLimit));
    return query(leadsRef, ...parts);
  }, [
    createdFilterDay,
    createdFilterMonth,
    createdFilterStatus,
    createdFilterYear,
    db,
    isSearchMode,
    pageSize,
    searchKind,
    sortField,
  ]);

  // Scan-based "contains" search fallback.
  // We only need this for name-part "contains" search. Phone-part uses `phoneSearch` index.
  const isContainsSearch = searchKind.kind === 'namePart';

  const leadNameLower = (l: any) => {
    const nl = String(l?.nameLower || '').toLowerCase();
    const n = String(l?.name || '').toLowerCase();
    return (nl || n).trim();
  };

  const sortLeadsClientSide = useCallback(
    (items: Lead[]) => {
      // Firestore ordering can be surprising if some docs have missing/mixed-type timestamps.
      // Do a final client-side sort so "Created ↓" and "Updated ↓" behave consistently.
      const key = (l: any) => {
        if (sortMode === 'updatedAtDesc') {
          return toMillisLoose(l?.updatedAt) || toMillisLoose(l?.createdAt) || 0;
        }
        return toMillisLoose(l?.createdAt) || 0;
      };
      return [...items].sort((a: any, b: any) => {
        const d = key(b) - key(a);
        if (d) return d;
        // Stable tie-breaker
        return String(a.id || '').localeCompare(String(b.id || ''));
      });
    },
    [sortMode]
  );

  const fetchContainsPage = async ({ after }: { after: any | null }) => {
    const leadsRef = collection(db, 'leads');
    const want = pageSize;
    const batchLimit = Math.max(want * 5, 250);
    const maxBatches = 10;
    const qName = searchKind.kind === 'namePart' ? String(searchKind.value || '').toLowerCase() : '';

    let cursor = after;
    let lastRaw: any | null = null;
    let lastBatchSize = 0;
    const out: any[] = [];
    const seen = new Set<string>();

    for (let b = 0; b < maxBatches && out.length < want; b += 1) {
      const parts: any[] = [orderBy(sortField, 'desc')];
      if (sortField !== 'createdAt') parts.push(orderBy('createdAt', 'desc'));
      if (cursor) parts.push(startAfter(cursor));
      parts.push(limit(batchLimit));

      // eslint-disable-next-line no-await-in-loop
      const snap = await getDocs(query(leadsRef, ...parts));
      lastBatchSize = snap.docs.length;
      if (!snap.docs.length) break;

      lastRaw = snap.docs[snap.docs.length - 1];
      cursor = lastRaw;

      for (const d of snap.docs) {
        const l = { id: d.id, ...(d.data() as any) } as any;
        if (seen.has(l.id)) continue;
        seen.add(l.id);

        const name = leadNameLower(l);
        if (!name.includes(qName)) continue;
        out.push(l);
        if (out.length >= want) break;
      }
    }

    // "hasMore" is conservative: if we filled the page, or we still have raw docs left to scan.
    const hasMoreScan = out.length >= want || (lastRaw && lastBatchSize === batchLimit);
    return {
      data: sortLeadsClientSide(filterPropertyLeads(out.slice(0, want) as Lead[])),
      last: lastRaw,
      hasMore: Boolean(hasMoreScan),
    };
  };

  const fetchExactPhoneResults = async () => {
    const leadsRef = collection(db, 'leads');
    const value = searchKind.kind === 'phone' ? searchKind.value : '';
    if (!value) {
      return { data: [] as Lead[], last: null, hasMore: false };
    }

    const [primarySnap, searchSnap] = await Promise.all([
      getDocs(query(leadsRef, where('phone10', '==', value), limit(pageSize))),
      getDocs(query(leadsRef, where('phoneSearch', 'array-contains', value), limit(pageSize))),
    ]);

    const docsById = new Map<string, any>();
    primarySnap.docs.forEach((d) => docsById.set(d.id, d));
    searchSnap.docs.forEach((d) => docsById.set(d.id, d));

    const data = filterPropertyLeads(
      Array.from(docsById.values()).map(
        (d) => ({ id: d.id, ...(d.data() as any) }) as Lead
      )
    );

    return {
      data: sortLeadsClientSide(data).slice(0, pageSize),
      last: null,
      hasMore: false,
    };
  };

  const loadFirstPage = async () => {
    setLoading(true);
    setError(null);
    try {
      if (searchKind.kind === 'phone') {
        const res = await fetchExactPhoneResults();
        setLeads(groupLeadsForSearch(res.data));
        setLastDoc(null);
        setHasMore(false);
      } else if (isContainsSearch) {
        const res = await fetchContainsPage({ after: null });
        setLeads(groupLeadsForSearch(sortLeadsClientSide(res.data)));
        setLastDoc(res.last);
        setHasMore(res.hasMore);
      } else {
        const snap = await getDocs(baseLeadsQuery);
        const data = filterPropertyLeads(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as Lead[]
        );
        setLeads(groupLeadsForSearch(sortLeadsClientSide(data)));
        const last = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
        setLastDoc(last);
        setHasMore(snap.docs.length === pageSize);
      }
    } catch (err: any) {
      console.error('Error fetching leads:', err);
      setError('Failed to fetch leads.');
      setLeads([]);
      setLastDoc(null);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (!hasMore || loadingMore || !lastDoc) return;
    if (searchKind.kind === 'phone') return;
    setLoadingMore(true);
    setError(null);
    try {
      if (isContainsSearch) {
        const res = await fetchContainsPage({ after: lastDoc });
        setLeads((prev) => {
          const seen = new Set(prev.map((x) => x.id));
          const merged = [...prev];
          for (const l of res.data) if (!seen.has(l.id)) merged.push(l);
          return groupLeadsForSearch(sortLeadsClientSide(merged));
        });
        setLastDoc(res.last);
        setHasMore(res.hasMore);
        return;
      }

      const leadsRef = collection(db, 'leads');
      const parts: any[] = [];
      const effectiveLimit = pageSize;
      if (isSearchMode) {
        if (searchKind.kind === 'phonePart') {
          parts.push(where('phoneSearch', 'array-contains', searchKind.value));
          parts.push(orderBy(sortField, 'desc'));
          if (sortField !== 'createdAt') parts.push(orderBy('createdAt', 'desc'));
        } else if (searchKind.kind === 'email') {
          parts.push(where('emailLower', '==', searchKind.value));
          parts.push(orderBy(sortField, 'desc'));
          if (sortField !== 'createdAt') parts.push(orderBy('createdAt', 'desc'));
        } else {
          parts.push(where('nameLower', '>=', searchKind.value));
          parts.push(where('nameLower', '<=', `${searchKind.value}\uf8ff`));
          parts.push(orderBy('nameLower', 'asc'));
        }
      } else {
        const r =
          createdFilterYear &&
          tsRangeForYMD({ year: createdFilterYear, month: createdFilterMonth, day: createdFilterDay });
        if (r) {
          parts.push(where('createdAt', '>=', r.start));
          parts.push(where('createdAt', '<', r.end));
          if (createdFilterStatus && createdFilterStatus !== NULL_STATUS_KEY) {
            parts.push(where('status', '==', createdFilterStatus));
          }
          parts.push(orderBy('createdAt', 'desc'));
          if (sortField === 'updatedAt') parts.push(orderBy('updatedAt', 'desc'));
        } else {
          if (createdFilterStatus && createdFilterStatus !== NULL_STATUS_KEY) {
            parts.push(where('status', '==', createdFilterStatus));
          }
          parts.push(orderBy(sortField, 'desc'));
          if (sortField !== 'createdAt') parts.push(orderBy('createdAt', 'desc'));
        }
      }
      parts.push(startAfter(lastDoc));
      parts.push(limit(effectiveLimit));
      const q = query(leadsRef, ...parts);

      const snap = await getDocs(q);
      const next = filterPropertyLeads(
        snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as Lead[]
      );
      setLeads((prev) => {
        const seen = new Set(prev.map((x) => x.id));
        const merged = [...prev];
        for (const l of next) if (!seen.has(l.id)) merged.push(l);
        return groupLeadsForSearch(sortLeadsClientSide(merged));
      });
      const last = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
      setLastDoc(last);
      setHasMore(snap.docs.length === effectiveLimit);
    } catch (err: any) {
      console.error('Error fetching more leads:', err);
      setError('Failed to load more leads.');
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    // Reset selection when the filter changes, because the selected lead might not be loaded anymore.
    closeLeadDetails();
    setLeads([]);
    setLastDoc(null);
    setHasMore(true);
    loadFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseLeadsQuery]);

  useEffect(() => {
    const statusesRef = collection(db, 'leadStatuses');
    const q = query(statusesRef, orderBy('order', 'asc'));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setStatuses(data);
      },
      (err) => {
        console.error('Error fetching statuses:', err);
      }
    );
    return () => unsubscribe();
  }, [db]);

  // users for assigned-to display name
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'users'),
      (snap) => setUsers(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }))),
      () => setUsers([])
    );
    return () => unsub();
  }, [db]);

  // Debounce search input for smoother typing.
  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchRaw.trim()), 200);
    return () => window.clearTimeout(t);
  }, [searchRaw]);

  // Build a complete (count-only) date index for the left panel.
  // Cached in sessionStorage to avoid repeated massive scans across page navigations.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const CACHE_KEY = 'crm_leads_date_index_cache';
        const CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes
        try {
          const raw = sessionStorage.getItem(CACHE_KEY);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed.savedAt === 'number' && Date.now() - parsed.savedAt < CACHE_TTL_MS && Array.isArray(parsed.rows)) {
              setDateIndexRows(parsed.rows);
              return;
            }
          }
        } catch {
          // ignore cache read errors
        }

        const leadsRef = collection(db, 'leads');
        const batchSize = 500;
        const maxDocs = 3000; // bounded to recent leads to prevent excessive egress bandwidth
        let cursor: any | null = null;
        const out: Array<{ createdAt: any; status: string }> = [];
        setDateIndexTruncated(false);

        while (!cancelled) {
          const qParts: any[] = [orderBy('createdAt', 'desc'), limit(batchSize)];
          if (cursor) qParts.splice(1, 0, startAfter(cursor));
          // eslint-disable-next-line no-await-in-loop
          const snap = await getDocs(query(leadsRef, ...qParts));
          if (!snap.docs.length) break;
          cursor = snap.docs[snap.docs.length - 1];
          for (const d of snap.docs) {
            const data = d.data() as any;
            if (isCareerApplicationSource(data?.source)) continue;
            const ca = data.createdAt;
            const caVal = ca?.toMillis ? ca.toMillis() : ca;
            out.push({ createdAt: caVal, status: String(data.status || '') });
          }
          if (out.length >= maxDocs) {
            setDateIndexTruncated(true);
            break;
          }
          if (snap.docs.length < batchSize) break;
        }

        if (cancelled) return;
        setDateIndexRows(out);
        try {
          sessionStorage.setItem(CACHE_KEY, JSON.stringify({ savedAt: Date.now(), rows: out }));
        } catch {
          // ignore cache write errors
        }
      } catch (e) {
        console.error('Failed to build date index', e);
        if (!cancelled) setDateIndexRows([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [db]);

  const createdDateTree = useMemo(() => {
    type Node = {
      total: number;
      years: {
        year: number;
        total: number;
        months: {
          month: number;
          total: number;
          days: {
            day: number;
            total: number;
            statuses: { id: string; label: string; total: number }[];
          }[];
        }[];
      }[];
    };

    const yearMap = new Map<number, any>();
    let total = 0;

    const statusLabelForId = (id: string) => {
      const s = statuses.find((x: any) => x.id === id);
      return s?.label || id;
    };

    // When search is active, show date-tree counts based on the currently loaded results
    // (quick/cheap behavior). Otherwise show counts across the full dataset.
    const rows: Array<{ createdAt: any; status: string }> = isSearchMode
      ? (leads || []).map((l) => ({ createdAt: (l as any).createdAt, status: String((l as any).status || '') }))
      : dateIndexRows;

    for (const row of rows) {
      const dt =
        row.createdAt && typeof (row.createdAt as any).toDate === 'function'
          ? (row.createdAt as any).toDate()
          : parseDateLoose(row.createdAt);
      if (!dt) continue;
      total += 1;
      const y = dt.getFullYear();
      const m = dt.getMonth() + 1;
      const d = dt.getDate();
      const statusId = row.status || '';

      if (!yearMap.has(y)) yearMap.set(y, new Map<number, any>());
      const monthMap = yearMap.get(y) as Map<number, any>;
      if (!monthMap.has(m)) monthMap.set(m, new Map<number, any>());
      const dayMap = monthMap.get(m) as Map<number, any>;
      if (!dayMap.has(d)) dayMap.set(d, new Map<string, number>());
      const statusMap = dayMap.get(d) as Map<string, number>;
      statusMap.set(statusId, (statusMap.get(statusId) || 0) + 1);
    }

    const years = Array.from(yearMap.entries())
      .map(([year, monthMap]) => {
        const months = Array.from((monthMap as Map<number, any>).entries())
          .map(([month, dayMap]) => {
            const days = Array.from((dayMap as Map<number, any>).entries())
              .map(([day, statusMap]) => {
                const statusesArr = Array.from((statusMap as Map<string, number>).entries())
                  .map(([id, cnt]) => ({ id, label: statusLabelForId(id), total: cnt }))
                  .sort((a, b) => b.total - a.total || a.label.localeCompare(b.label));
                const dayTotal = statusesArr.reduce((sum, s) => sum + s.total, 0);
                return { day, total: dayTotal, statuses: statusesArr };
              })
              .sort((a, b) => b.day - a.day);
            const monthTotal = days.reduce((sum, x) => sum + x.total, 0);
            return { month, total: monthTotal, days };
          })
          .sort((a, b) => b.month - a.month);
        const yearTotal = months.reduce((sum, x) => sum + x.total, 0);
        return { year, total: yearTotal, months };
      })
      .sort((a, b) => b.year - a.year);

    const out: Node = { total, years };
    return out;
  }, [dateIndexRows, isSearchMode, leads, statuses]);

  const statusFilterBaseLeads = useMemo(() => {
    if (!createdFilterYear) return leads;
    return leads.filter((l) => {
      const dt =
        l.createdAt && typeof (l.createdAt as any).toDate === 'function'
          ? (l.createdAt as any).toDate()
          : parseDateLoose((l as any).createdAt);
      if (!dt) return false;
      if (dt.getFullYear() !== createdFilterYear) return false;
      if (createdFilterMonth && dt.getMonth() + 1 !== createdFilterMonth) return false;
      if (createdFilterDay && dt.getDate() !== createdFilterDay) return false;
      return true;
    });
  }, [createdFilterDay, createdFilterMonth, createdFilterYear, leads]);

  const statusCountRows = useMemo(() => {
    return dateIndexRows.filter((row) => {
      if (!createdFilterYear) return true;
      const dt =
        row.createdAt && typeof (row.createdAt as any).toDate === 'function'
          ? (row.createdAt as any).toDate()
          : parseDateLoose(row.createdAt);
      if (!dt) return false;
      if (dt.getFullYear() !== createdFilterYear) return false;
      if (createdFilterMonth && dt.getMonth() + 1 !== createdFilterMonth) return false;
      if (createdFilterDay && dt.getDate() !== createdFilterDay) return false;
      return true;
    });
  }, [createdFilterDay, createdFilterMonth, createdFilterYear, dateIndexRows]);

  const statusFilterCounts = useMemo(() => {
    const map = new Map<string, number>();
    statusCountRows.forEach((row) => {
      const key = row.status ? String(row.status) : NULL_STATUS_KEY;
      map.set(key, (map.get(key) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([id, count]) => {
        const byId = statuses.find((s: any) => s.id === id);
        const byLabel = statuses.find(
          (s: any) =>
            typeof s?.label === 'string' &&
            String(s.label).trim().toLowerCase() === String(id).trim().toLowerCase()
        );
        const color = safeStatusColor(String((byId || byLabel)?.color || ''));
        return {
          id,
          label:
            id === NULL_STATUS_KEY
              ? 'No Status'
              : (statuses.find((s: any) => s.id === id)?.label as string) || id,
          count,
          color,
          order:
            typeof statuses.find((s: any) => s.id === id)?.order === 'number'
              ? Number(statuses.find((s: any) => s.id === id)?.order)
              : 9999,
        };
      })
      .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
  }, [statusCountRows, statuses]);

  const filteredLeads = useMemo(() => {
    if (!createdFilterStatus) return statusFilterBaseLeads;
    if (createdFilterStatus === NULL_STATUS_KEY) {
      return statusFilterBaseLeads.filter((l) => !l.status);
    }
    return statusFilterBaseLeads.filter((l) => l.status === createdFilterStatus);
  }, [createdFilterStatus, statusFilterBaseLeads]);

  useEffect(() => {
    let cancelled = false;
    const visible = filteredLeads.slice(0, 100);
    const pending = visible.filter((l: any) => {
      const base = Number(
        (l as any).siteVisitCount ?? (l as any).visitCount ?? (l as any).visits ?? (l as any).visitNos ?? 0
      ) || 0;
      return base <= 0;
    });
    if (!pending.length) return undefined;

    (async () => {
      try {
        const results = await Promise.all(
          pending.map(async (l) => {
            const c = await getCountFromServer(
              query(collection(db, 'leads', String(l.id), 'activities'), where('type', '==', 'visit'))
            );
            return [String(l.id), c.data().count || 0] as const;
          })
        );
        if (cancelled) return;
        setSiteVisitCountByLead((prev) => {
          const next = { ...prev };
          results.forEach(([id, count]) => {
            next[id] = count;
          });
          return next;
        });
      } catch {
        // keep UI stable even if count query fails for some leads
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [db, filteredLeads]);

  const userLabel = (email?: string | null) => {
    if (!email) return '—';
    const u = users.find((usr: any) => usr.email === email);
    if (u?.name) return u.name;
    const local = email.includes('@') ? email.split('@')[0] : email;
    return local || email;
  };

  const assignedLabelForLead = (l: Lead) => {
    if (l.assignedTo) return userLabel(l.assignedTo);
    if (l.assignedToName) return String(l.assignedToName);
    return '—';
  };

  const statusLabel = (statusId: string) => {
    const s = statuses.find((x: any) => x.id === statusId);
    return s?.label || statusId;
  };

  const statusColor = (statusId?: string | null): string | null => {
    if (!statusId) return null;
    const byId = statuses.find((x: any) => x.id === statusId);
    const norm = statusId.trim().toLowerCase();
    const byLabel = statuses.find(
      (x: any) => typeof x?.label === 'string' && String(x.label).trim().toLowerCase() === norm
    );
    const byStartsWithNew =
      norm === 'new'
        ? statuses.find((x: any) => typeof x?.label === 'string' && String(x.label).trim().toLowerCase().startsWith('new'))
        : null;
    const s = byId || byLabel || byStartsWithNew;
    const c = typeof (s as any)?.color === 'string' ? String((s as any).color).trim() : '';
    return /^#([0-9a-fA-F]{6})$/.test(c) ? c : null;
  };

  const renderCreatedDateFilterTree = (opts?: { variant?: 'inline' | 'panel' }) => {
    const variant = opts?.variant || 'inline';
    return (
      <Box
        sx={{
          mt: variant === 'inline' ? 1 : 0,
          px:
            variant === 'panel'
              ? 0
              : leadListView === 'sheet'
                ? { xs: 1.5, sm: 2.5 }
                : { xs: 0.5, sm: 0 },
          pb: variant === 'inline' ? 0.75 : 0,
        }}
      >
        <Box
          sx={{
            borderRadius: variant === 'panel' ? 0 : 2,
            border: variant === 'panel' ? 'none' : leadListView === 'sheet' ? 'none' : '1px solid',
            backgroundColor: variant === 'panel' ? 'transparent' : 'background.paper',
            // Inline variant: keep it visible but not too tall; scroll inside if needed.
            ...(variant === 'inline'
              ? {
                  maxHeight: { xs: 260, sm: 320 },
                  overflowY: 'auto',
                  pr: 0.5,
                  pb: 0.5,
                }
              : null),
            ...(variant === 'panel'
              ? null
              : leadListView === 'sheet'
                ? {
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    pt: 0.75,
                  }
                : {
                    borderColor: 'divider',
                    p: 0.75,
                  }),
          }}
        >
        <Chip
          clickable
          label={`All${createdDateTree.total ? ` (${createdDateTree.total})` : ''}`}
          onClick={() => {
            setCreatedFilterYear(null);
            setCreatedFilterMonth(null);
            setCreatedFilterDay(null);
            setCreatedFilterStatus(null);
          }}
          sx={{
            width: '100%',
            justifyContent: 'flex-start',
            px: 1.25,
            py: 1,
            borderRadius: 999,
            fontWeight: 700,
            backgroundColor: 'primary.lighter',
            '& .MuiChip-label': { width: '100%' },
          }}
        />

        <List dense disablePadding sx={{ mt: 1 }}>
          <Box>
            <ListItemButton
              selected={!!createdFilterStatus}
              onClick={() => setExpandedTopStatus((v) => !v)}
              sx={{
                borderRadius: 2,
                px: 1,
                '&.Mui-selected': { backgroundColor: 'primary.lighter' },
              }}
            >
              <ExpandMoreIcon
                fontSize="small"
                sx={{
                  mr: 0.5,
                  transform: expandedTopStatus ? 'rotate(0deg)' : 'rotate(-90deg)',
                  transition: 'transform 120ms ease',
                }}
              />
              <ListItemText primary="Status" primaryTypographyProps={{ fontWeight: 600 }} />
              <Chip
                size="small"
                label={statusCountRows.length}
                sx={{
                  height: 22,
                  fontWeight: 700,
                  color: 'text.secondary',
                  backgroundColor: 'grey.200',
                }}
              />
            </ListItemButton>

            <Collapse in={expandedTopStatus} timeout="auto" unmountOnExit>
              <List dense disablePadding sx={{ pl: 3 }}>
                {statusFilterCounts.map((s) => (
                  <ListItemButton
                    key={`top-status-${s.id}`}
                    selected={createdFilterStatus === s.id}
                    onClick={() => setCreatedFilterStatus(s.id)}
                    sx={{
                      borderRadius: 2,
                      px: 1,
                      mb: 0.6,
                      '&:last-of-type': { mb: 0 },
                      ...(s.color
                        ? {
                            border: '1px solid',
                            borderColor: alpha(s.color, 0.35),
                            backgroundColor:
                              createdFilterStatus === s.id
                                ? alpha(s.color, 0.24)
                                : alpha(s.color, 0.12),
                            '& .MuiListItemText-primary': {
                              color: alpha('#000', 0.8),
                              fontWeight: 700,
                            },
                            '&.Mui-selected': {
                              backgroundColor: alpha(s.color, 0.3),
                            },
                          }
                        : {
                            '&.Mui-selected': { backgroundColor: 'primary.lighter' },
                          }),
                    }}
                  >
                    <ListItemText primary={s.label} primaryTypographyProps={{ fontWeight: 600 }} />
                    <Chip
                      size="small"
                      label={s.count}
                      sx={{
                        height: 22,
                        fontWeight: 700,
                        color: s.color ? alpha('#000', 0.75) : 'text.secondary',
                        backgroundColor: s.color ? alpha(s.color, 0.2) : 'grey.200',
                        border: s.color ? `1px solid ${alpha(s.color, 0.32)}` : undefined,
                      }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          </Box>

          {createdDateTree.years.length === 0 && (
            <Box sx={{ px: 1, py: 1 }}>
              <Typography variant="body2" color="text.secondary">
                No leads with a valid received date found.
              </Typography>
              {dateIndexTruncated && (
                <Typography variant="caption" color="text.secondary">
                  Note: list truncated (showing first 20,000 leads).
                </Typography>
              )}
            </Box>
          )}
          {createdDateTree.years.map((y) => {
            const yKey = String(y.year);
            const yExpanded = expandedYears[yKey] ?? createdFilterYear === y.year;
            const yActive = createdFilterYear === y.year;
            return (
              <Box key={yKey}>
                <ListItemButton
                  selected={yActive}
                  onClick={() => {
                    setCreatedFilterYear(y.year);
                    setCreatedFilterMonth(null);
                    setCreatedFilterDay(null);
                    setCreatedFilterStatus(null);
                    setExpandedYears((prev) => ({
                      ...prev,
                      [yKey]: yActive ? !yExpanded : true,
                    }));
                  }}
                  sx={{
                    borderRadius: 2,
                    px: 1,
                    '&.Mui-selected': { backgroundColor: 'primary.lighter' },
                  }}
                >
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{
                      mr: 0.5,
                      transform: yExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                      transition: 'transform 120ms ease',
                    }}
                  />
                  <ListItemText primary={String(y.year)} primaryTypographyProps={{ fontWeight: 600 }} />
                  <Chip
                    size="small"
                    label={y.total}
                    sx={{
                      height: 22,
                      fontWeight: 700,
                      color: 'text.secondary',
                      backgroundColor: 'grey.200',
                    }}
                  />
                </ListItemButton>

                <Collapse in={yExpanded} timeout="auto" unmountOnExit>
                  <List dense disablePadding sx={{ pl: 2 }}>
                    {y.months.map((m) => {
                      const mKey = `${y.year}-${m.month}`;
                      const mExpanded =
                        expandedMonths[mKey] ?? (yActive && createdFilterMonth === m.month);
                      const mActive = yActive && createdFilterMonth === m.month;
                      return (
                        <Box key={mKey}>
                          <ListItemButton
                            selected={mActive}
                            onClick={() => {
                              setCreatedFilterYear(y.year);
                              setCreatedFilterMonth(m.month);
                              setCreatedFilterDay(null);
                              setCreatedFilterStatus(null);
                              setExpandedMonths((prev) => ({
                                ...prev,
                                [mKey]: mActive ? !mExpanded : true,
                              }));
                            }}
                            sx={{
                              borderRadius: 2,
                              px: 1,
                              '&.Mui-selected': { backgroundColor: 'primary.lighter' },
                            }}
                          >
                            <ExpandMoreIcon
                              fontSize="small"
                              sx={{
                                mr: 0.5,
                                transform: mExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                                transition: 'transform 120ms ease',
                              }}
                            />
                            <ListItemText primary={String(m.month)} primaryTypographyProps={{ fontWeight: 600 }} />
                            <Chip
                              size="small"
                              label={m.total}
                              sx={{
                                height: 22,
                                fontWeight: 700,
                                color: 'text.secondary',
                                backgroundColor: 'grey.200',
                              }}
                            />
                          </ListItemButton>

                          <Collapse in={mExpanded} timeout="auto" unmountOnExit>
                            <List dense disablePadding sx={{ pl: 2 }}>
                              {m.days.map((d) => {
                                const dKey = `${y.year}-${m.month}-${d.day}`;
                                const dExpanded =
                                  expandedDays[dKey] ?? (mActive && createdFilterDay === d.day);
                                const dActive = mActive && createdFilterDay === d.day;
                                return (
                                  <Box key={dKey}>
                                    <ListItemButton
                                      selected={dActive}
                                      onClick={() => {
                                        setCreatedFilterYear(y.year);
                                        setCreatedFilterMonth(m.month);
                                        setCreatedFilterDay(d.day);
                                        setCreatedFilterStatus(null);
                                        setExpandedDays((prev) => ({
                                          ...prev,
                                          [dKey]: dActive ? !dExpanded : true,
                                        }));
                                      }}
                                      sx={{
                                        borderRadius: 2,
                                        px: 1,
                                        '&.Mui-selected': { backgroundColor: 'primary.lighter' },
                                      }}
                                    >
                                      <ExpandMoreIcon
                                        fontSize="small"
                                        sx={{
                                          mr: 0.5,
                                          transform: dExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                                          transition: 'transform 120ms ease',
                                        }}
                                      />
                                      <ListItemText primary={String(d.day)} primaryTypographyProps={{ fontWeight: 600 }} />
                                      <Chip
                                        size="small"
                                        label={d.total}
                                        sx={{
                                          height: 22,
                                          fontWeight: 700,
                                          color: 'text.secondary',
                                          backgroundColor: 'grey.200',
                                        }}
                                      />
                                    </ListItemButton>

                                    <Collapse in={dExpanded} timeout="auto" unmountOnExit>
                                      <List dense disablePadding sx={{ pl: 3 }}>
                                        {d.statuses.map((s) => {
                                          const sActive = dActive && createdFilterStatus === s.id;
                                          return (
                                            <ListItemButton
                                              key={`${dKey}:${s.id}`}
                                              selected={sActive}
                                              onClick={() => {
                                                setCreatedFilterYear(y.year);
                                                setCreatedFilterMonth(m.month);
                                                setCreatedFilterDay(d.day);
                                                setCreatedFilterStatus(s.id);
                                              }}
                                              sx={{
                                                borderRadius: 2,
                                                px: 1,
                                                '&.Mui-selected': { backgroundColor: 'primary.lighter' },
                                              }}
                                            >
                                              <ListItemText primary={s.label} primaryTypographyProps={{ fontWeight: 600 }} />
                                              <Chip
                                                size="small"
                                                label={s.total}
                                                sx={{
                                                  height: 22,
                                                  fontWeight: 700,
                                                  color: 'text.secondary',
                                                  backgroundColor: 'grey.200',
                                                }}
                                              />
                                            </ListItemButton>
                                          );
                                        })}
                                      </List>
                                    </Collapse>
                                  </Box>
                                );
                              })}
                            </List>
                          </Collapse>
                        </Box>
                      );
                    })}
                  </List>
                </Collapse>
              </Box>
            );
          })}
        </List>
      </Box>
    </Box>
    );
  };

  // If the date tree filter changes, close the detail panel so we don't end up
  // showing a lead that is no longer in the filtered list.
  useEffect(() => {
    closeLeadDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdFilterYear, createdFilterMonth, createdFilterDay, createdFilterStatus]);

  // If user applies a received-date filter, force "Created time" sorting to avoid query/index issues.
  useEffect(() => {
    if (!createdFilterYear) return;
    setSortMode('createdAtDesc');
  }, [createdFilterYear]);

  // Close detail when clicking outside both the table paper and the drawer
  useEffect(() => {
    if (!detailLeadId) return;
    const handler = (e: MouseEvent) => {
      const now = Date.now();
      if (isResizing || now < resizeCooldownUntil) return;
      const target = e.target as HTMLElement;
      const insidePaper = target.closest('[data-role="leads-paper"]');
      const insideLeadArea = target.closest('[data-role="leads-area"]');
      const insideDrawer = target.closest('[data-role="lead-detail-drawer"]');
      if (!insidePaper && !insideLeadArea && !insideDrawer) {
        closeLeadDetails();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [closeLeadDetails, detailLeadId, isResizing, resizeCooldownUntil]);

  // The LeadDetailDrawer is fixed-position (portal), so we reserve its space via padding-right only.
  // Keeping the list pane at 100% avoids "double subtracting" (which caused a large gap).
  const detailOffset = detailDrawerOpen ? Math.max(detailWidth, 0) : 0;
  const listPaneWidth = '100%';
  const selectedLeadId = detailLeadId;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const isSuperAdmin = (roleId || '').toString() === 'super-admin';
  const showLeftDatePanel = isMdUp;

  const apptIndicator = (l: Lead): AppointmentBadge => {
    const appt = l.appointment;
    if (!appt || appt.status !== 'scheduled' || !appt.scheduledAt?.toDate) return null;
    const dt = appt.scheduledAt.toDate();
    const now = new Date();
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const isToday = dt >= start && dt <= end;
    const isPast = dt < now;
    const time = dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
    const date = dt.toLocaleDateString();
    // On mobile, keep the pill compact (omit meeting type prefix).
    const kind = isMobile ? '' : appt.type === 'office-meeting' ? 'Office' : '';
    const label = isToday ? `Today ${time}` : `${date} ${time}`;
    const full = kind ? `${kind} • ${label}` : label;
    const color = (isToday && !isPast ? 'success' : isPast ? 'warning' : 'default') as
      | 'default'
      | 'success'
      | 'warning';
    return { label: full, color };
  };

  const leadsListPaper = (
    <Paper
      elevation={0}
      sx={{
        p: leadListView === 'sheet' ? 0 : 1,
        width: '100%',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: leadListView === 'sheet' ? 0 : 1,
        boxShadow: 'none',
        border: 'none',
        backgroundColor: 'transparent',
      }}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      data-role="leads-paper"
      ref={paperRef}
    >
      {loading && (
        <Alert severity="info" sx={leadListView === 'sheet' ? { mx: { xs: 1.5, sm: 2.5 }, mt: 1 } : undefined}>
          Loading leads...
        </Alert>
      )}
      {!loading && leads.length === 0 && (
        <Alert severity="info" sx={leadListView === 'sheet' ? { mx: { xs: 1.5, sm: 2.5 }, mt: 1 } : undefined}>
          No leads found.
        </Alert>
      )}
      {!loading && filteredLeads.length === 0 && leads.length > 0 && (
        <Alert severity="info" sx={leadListView === 'sheet' ? { mx: { xs: 1.5, sm: 2.5 }, mt: 1 } : undefined}>
          No leads match your search.
        </Alert>
      )}
      {filteredLeads.map((l: Lead) => {
        const p10 = String((l as any).phone10 || l.phone || '').replace(/\D/g, '').slice(-10);
        return (
          <LeadCompactCard
            key={l.id}
            lead={{ ...(l as any), visitCount: siteVisitCountByLead[l.id] ?? (l as any).visitCount }}
            selected={selectedLeadId === l.id}
            onClick={() => openLeadDetails(l.id)}
            statusLabel={statusLabel(l.status)}
            assignedLabel={assignedLabelForLead(l)}
            badge={apptIndicator(l)}
            accentColor={statusColor(l.status)}
            view={leadListView}
            waUnreadCount={p10 ? (waUnreadByPhone.get(p10) ?? 0) : 0}
          />
        );
      })}

      {!loading && (
        <Box
          sx={{
            pt: 1,
            ...(leadListView === 'sheet' ? { px: { xs: 1.5, sm: 2.5 } } : null),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Chip
              label="All"
              size="small"
              clickable
              color={!hasActiveFilters ? 'primary' : 'default'}
              variant={!hasActiveFilters ? 'filled' : 'outlined'}
              onClick={() => {
                clearSearch();
                setSortMode('createdAtDesc');
                setCreatedFilterYear(null);
                setCreatedFilterMonth(null);
                setCreatedFilterDay(null);
                setCreatedFilterStatus(null);
              }}
            />
            <Chip
              label="50"
              size="small"
              clickable
              color={pageSize === 50 ? 'primary' : 'default'}
              variant={pageSize === 50 ? 'filled' : 'outlined'}
              onClick={() => setPageSize(50)}
            />
            <Chip
              label="100"
              size="small"
              clickable
              color={pageSize === 100 ? 'primary' : 'default'}
              variant={pageSize === 100 ? 'filled' : 'outlined'}
              onClick={() => setPageSize(100)}
            />
          </Stack>

          {hasMore ? (
            <Button variant="outlined" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? 'Loading...' : 'Load more'}
            </Button>
          ) : (
            <Box />
          )}
        </Box>
      )}
    </Paper>
  );

  const renderMobileSearchPanel = () => (
    isMobile && mobileSearchOpen ? (
      <Box
        sx={{
          mt: 0.75,
          mb: 1,
          px: { xs: 1.25, sm: 1.5 },
          py: 1.25,
          width: '100%',
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <TextField
          size="small"
          fullWidth
          placeholder="Search leads..."
          value={searchRaw}
          onChange={(e) => setSearchRaw(e.target.value)}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchRaw ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearchRaw('')}
                  edge="end"
                  aria-label="clear search"
                >
                  <CloseOutlinedIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>
    ) : null
  );

  return (
    <>
      <Box
        sx={{
          display: { xs: 'block', sm: 'flex' },
          width: '100%',
          pr: { xs: 0, sm: `${detailOffset}px` },
          transition: 'padding-right 0.2s ease',
          // In "AppSheet" list view, reduce the extra container padding so rows feel full-width.
          px: leadListView === 'sheet' ? 0 : { xs: 1, md: 1 },
          py: leadListView === 'sheet' ? 0 : 1,
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', sm: listPaneWidth },
            flexShrink: 0,
            minWidth: { sm: 320 },
            display: 'flex',
          flexDirection: 'column',
          gap: 2,
          height: { sm: '100%' },
        }}
          data-role="leads-area"
      >
          {leadListView === 'sheet' ? (
            <Paper
              elevation={0}
              sx={{
                // In sheet view we already remove the outer page padding, so keep this header
                // within the list pane width (avoid negative margins that can slide under the nav).
                width: '100%',
                borderRadius: 0,
                backgroundColor: 'background.paper',
                borderBottom: '1px solid',
                borderColor: 'divider',
                px: { xs: 1.5, sm: 2.5 },
                py: 1,
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="h5" noWrap sx={{ fontSize: 18, fontWeight: 700 }}>
                    All Leads
                  </Typography>
                  <IconButton size="small">
                    <ExpandMoreIcon />
                  </IconButton>
                  {!isMobile && (
                    <TextField
                      size="small"
                      placeholder="Search leads..."
                      value={searchRaw}
                      onChange={(e) => setSearchRaw(e.target.value)}
                      sx={{ width: { sm: 240, md: 320 } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchOutlinedIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: searchRaw ? (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setSearchRaw('')} edge="end" aria-label="clear search">
                              <CloseOutlinedIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                      }}
                    />
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', display: { xs: 'none', sm: 'block' } }}>
                    Showing {filteredLeads.length}
                    {hasMore ? '+' : ''}
                    {activeFiltersSummary}
                  </Typography>
                </Stack>

                <Stack direction="row" alignItems="center" gap={1}>
                  {isMobile && (
                    <IconButton
                      size="small"
                      aria-label={mobileSearchOpen ? 'Close search' : 'Search'}
                      onClick={() => {
                        setMobileSearchOpen((v) => !v);
                        if (mobileSearchOpen) setSearchRaw('');
                      }}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        color: 'primary.main',
                        backgroundColor: mobileSearchOpen ? 'primary.lighter' : 'transparent',
                      }}
                    >
                      {mobileSearchOpen ? <CloseOutlinedIcon fontSize="small" /> : <SearchOutlinedIcon fontSize="small" />}
                    </IconButton>
                  )}
                  <Tooltip title={`Sort: ${sortMode === 'updatedAtDesc' ? 'Updated time' : 'Created time'}`}>
                    <IconButton
                      size="small"
                      aria-label={`Sort by ${sortMode === 'updatedAtDesc' ? 'updated time' : 'created time'}`}
                      onClick={(e) => setSortAnchorEl(e.currentTarget)}
                      sx={{
                        border: '1px solid',
                        borderColor: isSortActive ? 'primary.main' : 'divider',
                        borderRadius: 2,
                        color: 'primary.main',
                        backgroundColor: isSortActive ? 'primary.lighter' : 'transparent',
                      }}
                    >
                      <SortOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {hasActiveFilters && (
                    <Tooltip title="Clear filters">
                      <IconButton
                        size="small"
                        aria-label="Clear filters"
                        onClick={() => {
                          clearSearch();
                          setSortMode('createdAtDesc');
                          setCreatedFilterYear(null);
                          setCreatedFilterMonth(null);
                          setCreatedFilterDay(null);
                          setCreatedFilterStatus(null);
                        }}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          color: 'text.secondary',
                        }}
                      >
                        <FilterAltOffOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {isAdmin && (
                    <Tooltip title="Switch to card view">
                      <IconButton
                        size="small"
                        aria-label="Switch to card view"
                        onClick={() => setLeadListView('card')}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          color: 'primary.main',
                          backgroundColor: 'primary.lighter',
                        }}
                      >
                        <ViewModuleOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setDrawerOpen(true)}
                    disabled={!canAction('leads','addLead')}
                    size={isMobile ? 'small' : 'medium'}
                    sx={{
                      fontSize: { xs: 12, sm: undefined },
                      minWidth: isMobile ? 0 : undefined,
                      px: isMobile ? 1 : undefined,
                      '& .MuiButton-startIcon': { mr: isMobile ? 0 : undefined },
                    }}
                  >
                    {isMobile ? null : 'New'}
                  </Button>
                  <IconButton
                    size="small"
                    aria-label="More"
                    onClick={(e) => setMoreAnchorEl(e.currentTarget)}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    anchorEl={sortAnchorEl}
                    open={sortMenuOpen}
                    onClose={() => setSortAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <MenuItem
                      selected={sortMode === 'createdAtDesc'}
                      onClick={() => {
                        setSortAnchorEl(null);
                        setSortMode('createdAtDesc');
                      }}
                    >
                      Created time (newest)
                    </MenuItem>
                    <MenuItem
                      selected={sortMode === 'updatedAtDesc'}
                      onClick={() => {
                        setSortAnchorEl(null);
                        setSortMode('updatedAtDesc');
                      }}
                    >
                      Updated time (recent)
                    </MenuItem>
                  </Menu>
                </Stack>
              </Stack>

              {renderMobileSearchPanel()}

              {!isMdUp && (hasCreatedDateFilter || hasStatusFilter) && (
                <Stack
                  direction="row"
                  spacing={0.75}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ mt: 1, pb: 0.25 }}
                >
                  <Chip
                    size="small"
                    label={`Year: ${createdFilterYear}`}
                    onDelete={() => {
                      setCreatedFilterYear(null);
                      setCreatedFilterMonth(null);
                      setCreatedFilterDay(null);
                      setCreatedFilterStatus(null);
                    }}
                    sx={{ borderRadius: 999 }}
                  />
                  {createdFilterMonth && (
                    <Chip
                      size="small"
                      label={`Month: ${createdFilterMonth}`}
                      onDelete={() => {
                        setCreatedFilterMonth(null);
                        setCreatedFilterDay(null);
                        setCreatedFilterStatus(null);
                      }}
                      sx={{ borderRadius: 999 }}
                    />
                  )}
                  {createdFilterDay && (
                    <Chip
                      size="small"
                      label={`Day: ${createdFilterDay}`}
                      onDelete={() => {
                        setCreatedFilterDay(null);
                        setCreatedFilterStatus(null);
                      }}
                      sx={{ borderRadius: 999 }}
                    />
                  )}
                  {createdFilterStatus && (
                    <Chip
                      size="small"
                      label={`Status: ${
                        createdFilterStatus === NULL_STATUS_KEY
                          ? 'No Status'
                          : (statuses.find((x: any) => x.id === createdFilterStatus)?.label as string) ||
                            createdFilterStatus
                      }`}
                      onDelete={() => setCreatedFilterStatus(null)}
                      sx={{ borderRadius: 999 }}
                    />
                  )}
                </Stack>
              )}

              {!isMdUp && renderCreatedDateFilterTree({ variant: 'inline' })}
            </Paper>
          ) : (
            <Box sx={{ mt: { xs: 1.5, sm: 0 } }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="h5"
                noWrap
                sx={{
                  fontSize: { xs: 18, sm: undefined },
                  fontWeight: { xs: 700, sm: undefined },
                }}
              >
                All Leads
              </Typography>
            <IconButton size="small">
              <ExpandMoreIcon />
            </IconButton>
              {!isMobile && (
                <TextField
                  size="small"
                  placeholder="Search leads..."
                  value={searchRaw}
                  onChange={(e) => setSearchRaw(e.target.value)}
                  sx={{ width: { sm: 240, md: 320 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchOutlinedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: searchRaw ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSearchRaw('')}
                          edge="end"
                          aria-label="clear search"
                        >
                          <CloseOutlinedIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              )}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ whiteSpace: 'nowrap', display: { xs: 'none', sm: 'block' } }}
              >
                Showing {filteredLeads.length}
                {hasMore ? '+' : ''}
                {activeFiltersSummary}
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" gap={1}>
              {isMobile && (
                <IconButton
                  size="small"
                  aria-label={mobileSearchOpen ? 'Close search' : 'Search'}
                  onClick={() => {
                    setMobileSearchOpen((v) => !v);
                    if (mobileSearchOpen) setSearchRaw('');
                  }}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    color: 'primary.main',
                    backgroundColor: mobileSearchOpen ? 'primary.lighter' : 'transparent',
                  }}
                >
                  {mobileSearchOpen ? (
                    <CloseOutlinedIcon fontSize="small" />
                  ) : (
                    <SearchOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              )}
              <Tooltip title={`Sort: ${sortMode === 'updatedAtDesc' ? 'Updated time' : 'Created time'}`}>
                <IconButton
                  size="small"
                  aria-label={`Sort by ${sortMode === 'updatedAtDesc' ? 'updated time' : 'created time'}`}
                  onClick={(e) => setSortAnchorEl(e.currentTarget)}
                  sx={{
                    border: '1px solid',
                    borderColor: isSortActive ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    color: 'primary.main',
                    backgroundColor: isSortActive ? 'primary.lighter' : 'transparent',
                  }}
                >
                  <SortOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {hasActiveFilters && (
                <Tooltip title="Clear filters">
                  <IconButton
                    size="small"
                    aria-label="Clear filters"
                    onClick={() => {
                      clearSearch();
                      setSortMode('createdAtDesc');
                      setCreatedFilterYear(null);
                      setCreatedFilterMonth(null);
                      setCreatedFilterDay(null);
                      setCreatedFilterStatus(null);
                    }}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      color: 'text.secondary',
                    }}
                  >
                    <FilterAltOffOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {isAdmin && (
                <Tooltip title="Switch to list view">
                  <IconButton
                    size="small"
                    aria-label="Switch to list view"
                    onClick={() => setLeadListView('sheet')}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      color: 'primary.main',
                      backgroundColor: 'transparent',
                    }}
                  >
                  <ViewAgendaOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              )}
              <Menu
                anchorEl={sortAnchorEl}
                open={sortMenuOpen}
                onClose={() => setSortAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem
                  selected={sortMode === 'createdAtDesc'}
                  onClick={() => {
                    setSortAnchorEl(null);
                    setSortMode('createdAtDesc');
                  }}
                >
                  Created time (newest)
                </MenuItem>
                <MenuItem
                  selected={sortMode === 'updatedAtDesc'}
                  onClick={() => {
                    setSortAnchorEl(null);
                    setSortMode('updatedAtDesc');
                  }}
                >
                  Updated time (recent)
                </MenuItem>
              </Menu>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setDrawerOpen(true)}
                disabled={!canAction('leads','addLead')}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  fontSize: { xs: 12, sm: undefined },
                  minWidth: isMobile ? 0 : undefined,
                  px: isMobile ? 1 : undefined,
                  '& .MuiButton-startIcon': { mr: isMobile ? 0 : undefined },
                }}
              >
                {isMobile ? null : 'New'}
              </Button>
              <IconButton
                size="small"
                aria-label="More"
                onClick={(e) => setMoreAnchorEl(e.currentTarget)}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={moreAnchorEl}
                open={moreMenuOpen}
                onClose={() => setMoreAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {isSuperAdmin && (
                  <MenuItem
                    onClick={() => {
                      setMoreAnchorEl(null);
                      setImportOpen(true);
                    }}
                  >
                    <UploadFileOutlinedIcon fontSize="small" style={{ marginRight: 8 }} />
                    Import CSV
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => {
                    setMoreAnchorEl(null);
                    setPageSize((s) => (s === 50 ? 100 : 50));
                  }}
                >
                  Page size: {pageSize}
                </MenuItem>
                {isAdmin && (
                  <MenuItem
                    onClick={() => {
                      setMoreAnchorEl(null);
                      setLeadListView((v) => (v === 'card' ? 'sheet' : 'card'));
                    }}
                  >
                    View: {leadListView === 'card' ? 'Cards' : 'List'}
                  </MenuItem>
                )}
                </Menu>
              </Stack>
              </Stack>

              {renderMobileSearchPanel()}

              {!isMdUp && (hasCreatedDateFilter || hasStatusFilter) && (
                <Stack
                  direction="row"
                  spacing={0.75}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ mt: 1, pb: 0.25, px: { xs: 0.5, sm: 0 } }}
                >
                  <Chip
                    size="small"
                    label={`Year: ${createdFilterYear}`}
                    onDelete={() => {
                      setCreatedFilterYear(null);
                      setCreatedFilterMonth(null);
                      setCreatedFilterDay(null);
                      setCreatedFilterStatus(null);
                    }}
                    sx={{ borderRadius: 999 }}
                  />
                  {createdFilterMonth && (
                    <Chip
                      size="small"
                      label={`Month: ${createdFilterMonth}`}
                      onDelete={() => {
                        setCreatedFilterMonth(null);
                        setCreatedFilterDay(null);
                        setCreatedFilterStatus(null);
                      }}
                      sx={{ borderRadius: 999 }}
                    />
                  )}
                  {createdFilterDay && (
                    <Chip
                      size="small"
                      label={`Day: ${createdFilterDay}`}
                      onDelete={() => {
                        setCreatedFilterDay(null);
                        setCreatedFilterStatus(null);
                      }}
                      sx={{ borderRadius: 999 }}
                    />
                  )}
                  {createdFilterStatus && (
                    <Chip
                      size="small"
                      label={`Status: ${
                        createdFilterStatus === NULL_STATUS_KEY
                          ? 'No Status'
                          : (statuses.find((x: any) => x.id === createdFilterStatus)?.label as string) ||
                            createdFilterStatus
                      }`}
                      onDelete={() => setCreatedFilterStatus(null)}
                      sx={{ borderRadius: 999 }}
                    />
                  )}
                </Stack>
              )}
              {!isMdUp && renderCreatedDateFilterTree({ variant: 'inline' })}
            </Box>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {showLeftDatePanel ? (
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                flex: 1,
                minHeight: 0,
              }}
            >
              <Box
                sx={{
                  width: 260,
                  flexShrink: 0,
                  borderRight: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'background.paper',
                }}
              >
                <Box sx={{ px: 1.5, py: 1 }}>
                  {renderCreatedDateFilterTree({ variant: 'panel' })}
                </Box>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>{leadsListPaper}</Box>
            </Box>
          ) : (
            leadsListPaper
          )}
        </Box>
      </Box>
      <AddLeadDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} onCreated={() => {}} onLeadSelect={openLeadDetails} />
        <Dialog open={importOpen} onClose={() => setImportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import (CSV)</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Leads</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Put your exported leads CSV at (supports columns like: leadKey, name, phone, status, assignedToName, nextFollowUpDate, source, projectName, unitType, brokerName, brokerId):
          </Typography>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.25,
              borderRadius: 1,
              bgcolor: 'grey.100',
              overflowX: 'auto',
              fontSize: 13,
            }}
          >
            swarna-crm-portal/import/leads.csv
          </Box>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>History (Activities)</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Put your exported activities/history CSV at:
          </Typography>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.25,
              borderRadius: 1,
              bgcolor: 'grey.100',
              overflowX: 'auto',
              fontSize: 13,
            }}
          >
            swarna-crm-portal/import/activities.csv
          </Box>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Brokers</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Put your exported brokers CSV at:
          </Typography>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.25,
              borderRadius: 1,
              bgcolor: 'grey.100',
              overflowX: 'auto',
              fontSize: 13,
            }}
          >
            swarna-crm-portal/import/brokers.csv
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
            Run imports from your terminal:
          </Typography>
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Note: for “Ref by” in Lead Details, importer maps <b>brokerName</b> → <b>referredBrokerName</b> and <b>brokerId</b> → <b>referredBrokerId</b>.
            brokerId should be the Firestore id from <b>/brokers</b> if you want a real link.
          </Alert>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.25,
              borderRadius: 1,
              bgcolor: 'grey.100',
              overflowX: 'auto',
              fontSize: 13,
            }}
          >
            cd swarna-crm-portal{'\n'}
            node scripts/import-leads.js --file import/leads.csv --dry-run{'\n'}
            node scripts/import-leads.js --file import/leads.csv{'\n'}
            node scripts/import-activities.js --file import/activities.csv --dry-run{'\n'}
            node scripts/import-activities.js --file import/activities.csv{'\n'}
            node scripts/import-brokers.js --file import/brokers.csv --dry-run{'\n'}
            node scripts/import-brokers.js --file import/brokers.csv{'\n'}
            node scripts/link-lead-assignees.js --dry-run{'\n'}
            node scripts/link-lead-assignees.js{'\n'}
            node scripts/merge-leads.js --phone 9876543210{'\n'}
            node scripts/merge-leads.js --primary LEAD_ID --secondary LEAD_ID --dry-run{'\n'}
            node scripts/merge-leads.js --primary LEAD_ID --secondary LEAD_ID{'\n'}
            node scripts/merge-duplicate-leads.js --dry-run{'\n'}
            node scripts/merge-duplicate-leads.js --limit-phones 100{'\n'}
            node scripts/merge-duplicate-leads.js{'\n'}
            node scripts/delete-leads-missing-phone.js --dry-run{'\n'}
            node scripts/delete-leads-missing-phone.js --limit 200 --dry-run{'\n'}
            node scripts/delete-leads-missing-phone.js --limit 200{'\n'}
            node scripts/delete-leads-missing-phone.js{'\n'}
            node scripts/wipe-all-leads.js --dry-run{'\n'}
            node scripts/wipe-all-leads.js --confirm DELETE_ALL_LEADS
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <LeadDetailDrawer
        open={detailDrawerOpen}
        lead={detailLead}
        onClose={closeLeadDetails}
        onExited={handleLeadDrawerExited}
        onUpdated={(next) => {
          setLeads((prev) => prev.map((l) => (l.id === next.id ? ({ ...l, ...next } as Lead) : l)));
          setDetailLeadFallback((prev) => (prev?.id === next.id ? ({ ...prev, ...next } as Lead) : prev));
        }}
        onDeleted={(leadId) => {
          setLeads((prev) => prev.filter((l) => l.id !== leadId));
          if (detailLeadId === leadId) closeLeadDetails();
        }}
        onWidthChange={(w) => {
          const minList = 340;
          const maxDrawer = 700;
          const minDrawer = 320;
          const viewport = typeof window !== 'undefined' ? window.innerWidth : 1200;
          const maxAllowed = Math.max(minDrawer, Math.min(maxDrawer, viewport - minList));
          const clamped = Math.min(maxAllowed, Math.max(minDrawer, w || minDrawer));
          setDetailWidth(clamped);
        }}
        onResizingChange={(r) => {
          setIsResizing(r);
          if (!r) setResizeCooldownUntil(Date.now() + 250);
        }}
        mobileFullScreen={isMobile}
      />
    </>
  );
}
